import React, { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Select, message } from "antd";
import { HomeSeriesContent } from "./HomeSeriesContent";
import { useHomeSeriesSurface } from "./useHomeSeriesSurface";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage, AutoModel } from "../../components/common";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { createSeriesApi, updateSeriesNameApi } from "../../service/series";
import { deleteFolderApi } from "../../service/folders";
import { getLanguageCodeFromName, getSourceLanguageOptions } from "../../utils/languageUtils";
import type { HomeSeriesFolderItem } from "./types";
import {
  OUTER_SERIES_LIST_PAGE_PARAM,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
} from "../../utils/menuPathNormalize";
import { usePremiumWorkspaceListMeta } from "./premiumWorkspaceContext";
import "./homeModals.scss";

interface Props {}

/**
 * 精品译制首页：列表走 getSeriesListApi；创建 / 重命名 / 删除与权限对齐。
 */
export const Home: FC<Props> = () => {
  const navigate = useNavigate();
  const workspaceListMeta = usePremiumWorkspaceListMeta();
  const surface = useHomeSeriesSurface();
  const { hasCreateSeriesPermission, hasSeriesDeletePermission } = useSeriesButtonPermissions();
  const { alertConfig, hideAlert, showSuccess, showError } = useAlert();

  const [renameModal, setRenameModal] = useState<{
    visible: boolean;
    item: HomeSeriesFolderItem | null;
    newName: string;
  }>({ visible: false, item: null, newName: "" });

  useEffect(() => {
    workspaceListMeta.setListTotalCount(surface.totalCount);
  }, [surface.totalCount, workspaceListMeta]);

  const [createSeriesModal, setCreateSeriesModal] = useState<{
    visible: boolean;
    seriesName: string;
    sourceLanguage: string;
    subtitleErase: boolean;
  }>({
    visible: false,
    seriesName: "",
    sourceLanguage: "中文",
    subtitleErase: false,
  });

  const onFolderOpen = useCallback(
    (item: HomeSeriesFolderItem) => {
      if (!item.id) {
        message.warning("剧集ID不存在，无法打开剧集详情");
        return;
      }
      const q = new URLSearchParams();
      q.set("seriesId", String(item.id));
      q.set("seriesName", item.name || "");
      if (surface.page > 1) {
        q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(surface.page));
      }
      navigate(`${PREMIUM_SERIES_INNER_BASE}?${q.toString()}`);
    },
    [navigate, surface.page]
  );

  const onNewSeries = useCallback(() => {
    if (!hasCreateSeriesPermission()) {
      showError("暂无创建剧集权限");
      return;
    }
    setCreateSeriesModal({
      visible: true,
      seriesName: "",
      sourceLanguage: "中文",
      subtitleErase: false,
    });
  }, [hasCreateSeriesPermission, showError]);

  const onRename = useCallback((item: HomeSeriesFolderItem) => {
    setRenameModal({
      visible: true,
      item,
      newName: item.name || "",
    });
  }, []);

  const onDelete = useCallback(
    async (item: HomeSeriesFolderItem) => {
      if (!hasSeriesDeletePermission()) {
        showError("暂无删除权限");
        return;
      }
      try {
        await deleteFolderApi(item.id);
        showSuccess(`剧集「${item.name}」删除成功`);
        await surface.refresh();
      } catch {
        showError("删除剧集失败，请稍后重试");
      }
    },
    [hasSeriesDeletePermission, showError, showSuccess, surface]
  );

  const handleRenameOk = useCallback(async () => {
    if (!renameModal.item || !renameModal.newName.trim()) {
      return;
    }
    const originalName = renameModal.item.name || "";
    const trimmed = renameModal.newName.trim();
    if (originalName === trimmed) {
      setRenameModal({ visible: false, item: null, newName: "" });
      return;
    }
    try {
      const res = await updateSeriesNameApi({
        series_id: renameModal.item.id,
        name: trimmed,
      });
      if (res.code === 0 && res.data) {
        showSuccess("重命名成功");
        setRenameModal({ visible: false, item: null, newName: "" });
        await surface.refresh();
      } else {
        showError(res.msg || "重命名失败，请稍后重试");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "重命名失败，请稍后重试";
      showError(msg);
    }
  }, [renameModal.item, renameModal.newName, showError, showSuccess, surface]);

  const createSeries = useCallback(async () => {
    const { seriesName, sourceLanguage, subtitleErase } = createSeriesModal;
    if (!seriesName.trim()) {
      showError("请输入剧集名称");
      return;
    }
    try {
      message.loading("正在创建剧集...", 0);
      const sourceLang = getLanguageCodeFromName(sourceLanguage);
      const response = await createSeriesApi({
        name: seriesName.trim(),
        sourceLangCode: sourceLang,
        needRemovalSubtitle: subtitleErase,
      });
      message.destroy();
      if (response && response.code !== 0) {
        showError(response.msg || "创建剧集失败，请稍后重试");
        return;
      }
      showSuccess("剧集创建成功");
      setCreateSeriesModal({
        visible: false,
        seriesName: "",
        sourceLanguage: "中文",
        subtitleErase: false,
      });
      const newSeriesId = response?.data;
      if (typeof newSeriesId === "number") {
        const encodedName = encodeURIComponent(seriesName.trim());
        navigate(
          `${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?seriesId=${newSeriesId}&seriesName=${encodedName}&isPure=true`
        );
      } else {
        await surface.refresh();
      }
    } catch (error: unknown) {
      message.destroy();
      const err = error as { response?: { data?: { msg?: string } }; message?: string };
      const errorMessage =
        err?.response?.data?.msg || err?.message || "创建剧集失败，请稍后重试";
      showError(errorMessage);
    }
  }, [createSeriesModal, navigate, showError, showSuccess, surface]);

  return (
    <>
      <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.MY_WORK} />
      <HomeSeriesContent
        embeddedInWorkspace
        totalCount={surface.totalCount}
        pageItems={surface.pageItems}
        page={surface.page}
        pageCount={surface.pageCount}
        hasPrev={surface.hasPrev}
        hasNext={surface.hasNext}
        isLoading={surface.isLoading}
        searchInputValue={surface.searchInputValue}
        onSearchChange={surface.handleSearchChange}
        timeSort={surface.timeSort}
        onTimeSortChange={surface.setTimeSort}
        scope={surface.scope}
        onScopeChange={surface.setScope}
        showScopeFilter={surface.showScopeFilter}
        onPagePrev={surface.goPrev}
        onPageNext={surface.goNext}
        onFolderOpen={onFolderOpen}
        onNewSeries={onNewSeries}
        onRename={onRename}
        onDelete={onDelete}
        canCreateSeries={hasCreateSeriesPermission()}
        canDeleteSeries={hasSeriesDeletePermission()}
      />

      <AutoModel
        open={renameModal.visible}
        onClose={() => setRenameModal({ visible: false, item: null, newName: "" })}
        title="重命名剧集"
        contentClassName="home-series-modal"
        footerRight={<>
          <Button onClick={() => setRenameModal({ visible: false, item: null, newName: "" })}>取消</Button>
          <Button type="primary" onClick={() => void handleRenameOk()}>确定</Button>
        </>}
      >
        <Input
          value={renameModal.newName}
          onChange={(e) => setRenameModal((prev) => ({ ...prev, newName: e.target.value }))}
          placeholder="请输入剧集名称"
          autoFocus
        />
      </AutoModel>

      <AutoModel
        open={createSeriesModal.visible}
        onClose={() =>
          setCreateSeriesModal({
            visible: false,
            seriesName: "",
            sourceLanguage: "中文",
            subtitleErase: false,
          })
        }
        title="新建剧集"
        contentClassName="home-series-modal"
        footerRight={<>
          <Button
            onClick={() =>
              setCreateSeriesModal({
                visible: false,
                seriesName: "",
                sourceLanguage: "中文",
                subtitleErase: false,
              })
            }
          >
            取消
          </Button>
          <Button type="primary" onClick={() => void createSeries()}>创建</Button>
        </>}
      >
        <div className="create-episode-form">
          <div className="form-item">
            <label>剧集名称</label>
            <Input
              value={createSeriesModal.seriesName}
              onChange={(e) =>
                setCreateSeriesModal((prev) => ({ ...prev, seriesName: e.target.value }))
              }
              placeholder="请输入剧集名称"
              autoFocus
            />
          </div>
          <div className="form-item">
            <label>源语言</label>
            <Select
              style={{ width: "100%" }}
              value={createSeriesModal.sourceLanguage}
              onChange={(value) =>
                setCreateSeriesModal((prev) => ({ ...prev, sourceLanguage: value }))
              }
              placeholder="请选择源语言"
              dropdownStyle={{ zIndex: 1001 }}
              getPopupContainer={(triggerNode) =>
                (triggerNode.parentNode as HTMLElement | null) ?? document.body
              }
            >
              {getSourceLanguageOptions().map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="form-item">
            <div className="subtitle-toggle-wrapper">
              <label>字幕擦除</label>
              <div
                className={`subtitle-toggle ${createSeriesModal.subtitleErase ? "active" : ""}`}
                onClick={() =>
                  setCreateSeriesModal((prev) => ({
                    ...prev,
                    subtitleErase: !prev.subtitleErase,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setCreateSeriesModal((prev) => ({
                      ...prev,
                      subtitleErase: !prev.subtitleErase,
                    }));
                  }
                }}
                role="switch"
                tabIndex={0}
                aria-checked={createSeriesModal.subtitleErase}
              >
                <div className="toggle-handle" />
              </div>
            </div>
          </div>
        </div>
      </AutoModel>
    </>
  );
};
